import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CustomTooltips } from '@coreui/coreui-plugin-chartjs-custom-tooltips';
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import ChartAnnotation from 'chartjs-plugin-annotation';
import * as pluginDataLabels from 'chartjs-plugin-datalabels';
import moment, { Moment } from 'moment';
import { BaseChartDirective, Label } from 'ng2-charts';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { TranslatorService } from '../../../helpers/getTranslationsHelper/translator.service';
import { LanguageService } from '../../../language.service';
import { SensorEventsService } from '../../../services/sensor-events.service';


export interface CustomStatistics {
  occupancy: number;
  parking_events: number;
  timestamp: string | Moment;
}
export interface Filters {
  starts_after?: startsAfterFilter;
  ends_before?: endsBeforeFilter;
}

export type startsAfterFilter = null | string;
export type endsBeforeFilter = null | string;

@Component({
  selector: 'app-statistics-card',
  templateUrl: './statistics-card.component.html',
  styleUrls: ['./statistics-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatisticsCardComponent implements OnInit, OnDestroy {
  public dateExactTime$: BehaviorSubject<string | null> = new BehaviorSubject(null)

  @ViewChildren(BaseChartDirective) baseChart: QueryList<BaseChartDirective>;
  @Input('filters') set filters(filters: Filters) {
    this.period = filters;

    if (this.period != null && (this.period.starts_after != null && this.period.ends_before != null)) {
      //@ts-ignore
      this.allDates = [...this.getDaysBetweenDates(moment(this.period.starts_after), moment(this.period.ends_before))]

      this.dateExactTime$.next(`${moment(this.period.starts_after).format('DD-MM-YYYY')} - ${moment(this.period.ends_before).format('DD-MM-YYYY')}`)
    }

  }
  @Input('clearArrays') set clearArrays(value: boolean) {
    //if (!value) this.dateExactTime$.next(null)
    this.clearEverything()
    this._clearArrays = value;

  }

  private allDates: any[] = [];

  private finalTimestamps: any[] = [];

  public period: Filters = { starts_after: null, ends_before: null }
  private _clearArrays: boolean = false;

  public className: string;
  public cards_section_1: any[];
  public cards_section_2: any[];
  public cards_section_3: any[];

  public sections: any[] = []

  private colors: any = {
    backgroundColor: 'slategray',
    hoverBackgroundColor: '#38aaf7',
    barThickness: 3,
    maxBarThickness: 8,
  }
  private options: (ChartOptions & { annotation: any, plugins: { datalabels: { display: false } } }) = {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        fontColor: '#fff',
        fontSize: 15
      }
    },
    tooltips: {
      enabled: false,
      custom: CustomTooltips,
      mode: 'index',
      //enabled: false,
      intersect: false,
    },
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      xAxes: [{
        display: true,
        type: 'time',
        distribution: 'series',
        offset: true,
        time: {
          unit: 'day',
          displayFormats: {
            'millisecond': 'MMM DD',
            'second': 'MMM DD',
            'minute': 'MMM DD',
            'hour': 'MMM DD',
            'day': 'MMM DD',
            'week': 'MMM DD',
            'month': 'MMM DD',
            'quarter': 'MMM DD',
            'year': 'MMM DD',
          }
        },
        gridLines: {
          color: 'transparent',
          zeroLineColor: 'transparent'
        },
        ticks: {
          autoSkip: true,
          autoSkipPadding: 20,
          fontSize: 13,
          fontColor: '#fff',
          minRotation: 45,
          maxRotation: 65
        }

      }],
      yAxes: [{
        display: true,
        gridLines: {
          color: 'transparent',
          zeroLineColor: 'transparent'
        },
        ticks: {
          callback: function (label, index, labels) {
            let formatted;
            if (label <= 1) {
              formatted = Number(label).toFixed(1)
            } else formatted = label
            return formatted
          },
          display: true,
          fontSize: 12,
          fontStyle: 'Bold',
          fontColor: '#fff'
        }
      }],
    },
    // elements: {
    //   line: {
    //     borderWidth: 5,
    //     borderColor: '#263238',
    //     fill: false,
    //     backgroundColor: '#263238',
    //   },
    //   point: {
    //     radius: 4,
    //     hitRadius: 5,
    //     hoverRadius: 6,
    //   },
    // },
    annotation: {},
    plugins: {
      datalabels: {
        display: false,
      }
    }
  };
  public lineChartPlugins = [ChartAnnotation];

  // Occupancy Day Chart
  public occupancyMonthChartData: ChartDataSets[] = [{ data: [{ x: moment(), y: 0 }], label: 'Occupancy %', type: 'bar', borderColor: 'slategray' }];
  public occupancyMonthChartLabels: Label = [];
  public occupancyMonthChartOptions: (ChartOptions & { annotation: any, plugins: { datalabels: { display: false } } }) = this.options
  public occupancyMonthChartColours: Array<any> = [this.colors];
  public occupancyMonthChartLegend = true;
  public occupancyMonthChartType: ChartType = 'bar'
  public occupancyMonthChartPlugins = [pluginDataLabels, ChartAnnotation];

  // Events Month Chart
  public eventsMonthChartData: ChartDataSets[] = [{ data: [{ x: moment(), y: 0 }], label: 'Parking Events', type: 'bar', borderColor: 'slategray' }];
  public eventsMonthChartLabels: Label = [];
  public eventsMonthChartOptions: (ChartOptions & { annotation: any, plugins: { datalabels: { display: false } } }) = this.options
  public eventsMonthChartColours: Array<any> = [this.colors];
  public eventsMonthChartLegend = true;
  public eventsMonthChartType: ChartType = 'bar'
  public eventsMonthChartPlugins = [pluginDataLabels, ChartAnnotation];

  private statsSubscription: Subscription;

  public monthDataIsNull: BehaviorSubject<boolean>

  private getDaysArrayByMonth() {
    const startDate = moment().startOf('month')

    const endDate = moment()

    const difference = endDate.diff(startDate, 'days')

    // var daysInMonth = moment().daysInMonth();
    let daysInMonth = difference + 1;
    const arrDays = [];

    while (daysInMonth) {
      const current = moment().date(daysInMonth).format('DD/MM');
      arrDays.push(current);
      daysInMonth--;
    }
    return arrDays.reverse();
  }
  public monthDays: any[] = this.getDaysArrayByMonth();
  // public monthDays: any[] = moment.months();

  public weekDays: any[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  public dayHours: any[];

  public maxOccupancy = '0';
  public timeOccupancy: any;
  public maxEvents = '0';
  public timeEvents: any;
  public maxOccupancy_Day = '0';
  public timeOccupancy_Day: any;
  public maxEvents_Day = '0';
  public timeEvents_Day: any;
  public maxOccupancy_Month = '0';
  public timeOccupancy_Month: any;
  public maxEvents_Month = '0';
  public timeEvents_Month: any;

  public avgOccupancy = '0';
  public avgOccupancyOverall = '0';
  public date: string = moment().format('DD/MM/YYYY');
  public weekDateRange: string;
  public monthDateRange: string;

  public customData$: Observable<any[]>

  public timestampsLabels: any[] = [];

  public occupancyData: any[] = [{ x: moment(), y: 0 }];
  public pEventsData: any[] = [{ x: moment(), y: 0 }];

  public occupancies: number[] = [];
  public pEvents: number[] = [];

  private langSub: Subscription;

  public parkingEventsSum: number = 0;
  public occupancySum: number = 0;

  constructor(
    private translatorService: TranslatorService,
    private translateService: LanguageService,
    private sensorEventsService: SensorEventsService) {
    this.monthDataIsNull = new BehaviorSubject(true);
  }

  ngOnInit() {
    console.log("2", this.parkingEventsSum)
    this.dayHours = this.dayHoursFunction();
    const previousWeekStartDate = moment().subtract(1, 'weeks').startOf('isoWeek').format('DD/MM/YYYY')
    const endOfPreviousWeek = moment().subtract(1, 'weeks').startOf('isoWeek').add(6, 'day').format('DD/MM/YYYY')
    this.weekDateRange = `${moment().startOf('isoWeek').format('DD/MM/YYYY')} - ${moment().endOf('isoWeek').format('DD/MM/YYYY')}`
    this.monthDateRange = `${moment().startOf('month').format('DD/MM/YYYY')} - ${moment().subtract(1, 'day').format('DD/MM/YYYY')}`

    this.languageTranslations()

    this.cards_section_1 = [
      {
        id: 1,
        name: 'occupancy_hour',
        className: 'purple',
        title: 'SENSORS.OCCUPANCY',
        title2: 'SENSORS.HOUR',
        title3: 'SENSORS.PERIOD',
        back: {
          title1: 'SENSOR_STATS.OCCUPANCY_HOUR_1',
          title2: 'SENSOR_STATS.OCCUPANCY_HOUR_2'
        }
      },
      {
        id: 2,
        name: 'events_hour',
        className: 'purple',
        title: 'DASHBOARD.CHARTS_EXPANSION_PANEL.CHART_PARKING_EVENTS',
        title2: 'SENSORS.HOUR',
        title3: 'SENSORS.PERIOD',
        back: {
          title1: 'SENSOR_STATS.EVENTS_HOUR_1',
          title2: 'SENSOR_STATS.EVENTS_HOUR_2'
        }
      }]

    this.sections = [
      this.cards_section_1
    ]

    this.statsSubscription = this.sensorEventsService.getStats().pipe(distinctUntilChanged())
      .subscribe(data => {
        if (data && data !== null) {
          Object.keys(data).length ? this.refineData(data) : (() => { return })
          Object.keys(data).length ? this.monthDataIsNull.next(false) : this.monthDataIsNull.next(true)
        } else {
          this.monthDataIsNull.next(true)
        }
      })

    this.sensorEventsService.getSumStats().pipe(distinctUntilChanged())
      .subscribe(data => {
        if (data) {
        data.map(item => {
          this.parkingEventsSum = item['parking_events']
          this.occupancySum = item['occupancy'] <= 1 ? item['occupancy'] * 100 : item['occupancy']
        })
      }
      })
  }

  getAverageOccupancyProgressBarWidth() {
    return (this.occupancySum).toFixed(2).toString();
  }

  languageTranslations() {
    this.langSub = this.translateService.currentLanguage
      .pipe(distinctUntilChanged())
      .subscribe(res => {
        if (res === 'en') {
          moment.locale('en-gb');
          this.weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
        } else {
          moment.locale('el');
          this.weekDays = ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή']
        }

        if (this.baseChart) {
          this.baseChart.forEach(chart => chart.update())
        }
      })
  }

  getDaysBetweenDates = function (startDate, endDate) {
    let dates = [];
    for (var m = moment(startDate); m.isBefore(endDate); m.add(1, 'days')) {
      dates.push(m.format('YYYY-MM-DD'));
    }
    return dates
  };

  dayHoursFunction = () => {
    return Array.from({ length: 24 }, (_, i) => i).reduce((r, hour) => {
      r.push(moment({ hour, minute: 0 }).format('h:mm A'));
      return r;
    }, []);
  }

  refineData(data?: CustomStatistics[]) {
    data.map((item, index) => {
      item['occupancy'] = Number(item['occupancy']) <= 1
        ? Number((item['occupancy'] * 100).toFixed(2))
        : item['occupancy']
      item['parking_events'] = item['parking_events']
      item['timestamp'] = moment(item['timestamp'])
      //this.timestampsLabels.push(item['timestamp'])
      this.timestampsLabels.push(this.allDates[index])

      //this.occupancyData.push({ x: item['timestamp'], y: item['occupancy'] })
      this.occupancyData.push({ x : this.allDates[index], y: item['occupancy'] })
      this.occupancies.push(item['occupancy'])
      //this.pEventsData.push({ x: item['timestamp'], y: item['parking_events'] })
      this.pEventsData.push({ x: this.allDates[index], y: item['parking_events'] })
      this.pEvents.push(item['parking_events'])
      return item
    })

    var j, i, lengthOfA, lengthOfB;
    i = j = 0;
    lengthOfA = this.timestampsLabels.length;
    lengthOfB = this.allDates.length;

    while (i < lengthOfA && j < lengthOfB) {
      if (this.timestampsLabels[i] === this.allDates[j]) {
        this.finalTimestamps.push(this.timestampsLabels[i++]);
        j++;
      } else if (this.timestampsLabels[i] < this.allDates[j]) {
        this.finalTimestamps.push(this.timestampsLabels[i++]);
      } else {
        this.finalTimestamps.push(this.allDates[j++]);
      }
    }
    if (i === lengthOfA) {
      for (; j < lengthOfB; j++) {
        this.finalTimestamps.push(this.allDates[j]);
      }
    } else {
      for (; i < lengthOfA; i++) {
        this.finalTimestamps.push(this.timestampsLabels[i]);
      }
    }

    //@ts-ignore
    let maxOccupancy = Math.max(...this.occupancies)
    let idxOccupancy = this.findLastIndex(this.occupancies, maxOccupancy)
    this.maxOccupancy = `${maxOccupancy.toString()} %`
    this.timeOccupancy = ` ${moment(this.timestampsLabels[idxOccupancy]).format('DD-MM-YYYY')}`

    let maxEvents = Math.max(...this.pEvents)
    let idxEvents = this.findLastIndex(this.pEvents, maxEvents)

    this.maxEvents = maxEvents.toString()
    this.timeEvents = ` ${moment(this.timestampsLabels[idxEvents]).format('DD-MM-YYYY')}`

    this.createCharts(data)
  }

  createCharts(data) {

    this.occupancyMonthChartData[0].data = this.occupancyData
    this.occupancyMonthChartLabels = this.finalTimestamps

    this.eventsMonthChartData[0].data = this.pEventsData
    this.eventsMonthChartLabels = this.finalTimestamps
  }

  momentizePeriod(filters) {
    this.period.starts_after = moment(filters.starts_after).format('DD-MM-YYYY')
    this.period.ends_before = moment(filters.ends_before).format('DD-MM-YYYY')
  }

  clearEverything() {
    this.parkingEventsSum = 0;
    this.occupancySum = 0;

    this.timeOccupancy = undefined;
    this.timeEvents = undefined;
    this.maxOccupancy = '0';
    this.maxEvents = '0';

    this.finalTimestamps = [];
    // this.allDates = [];
    this.timestampsLabels = [];
    this.occupancyData = [];
    this.pEventsData = [];
    this.occupancies = [];
    this.pEvents = [];
    this.period = null;
    this.monthDataIsNull.next(true)
  }

  findLastIndex(array, searchValue) {
    const index = array.slice().reverse().findIndex(x => x === searchValue);
    const count = array.length - 1
    const finalIndex = index >= 0 ? count - index : index;
    return finalIndex;
  }

  ngOnDestroy() {
    console.log("1", this.parkingEventsSum)
    this.clearEverything()
    this.sensorEventsService.setSumCustomStats(null)
    //this.sensorEventsService.clearSumStats()
    if (this.langSub) this.langSub.unsubscribe();
    if (this.statsSubscription) this.statsSubscription.unsubscribe();
  }

}
